import type { RefObject } from 'react'

import { PrescriptionStates } from '~/constants/prescription'

import type { TaskSectionHandle } from '../TaskSection'
import type { SwapProductRefillHandle } from './SwapProductRefill'

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

export interface SwapProductValidationDependencies {
  automaticallyCreateTaskValue: boolean | undefined
  doctorFieldRef: RefObject<HTMLSelectElement>
  doctorIdValue: string | undefined
  doctorValidationMessage: string
  formRef: RefObject<HTMLFormElement>
  instructionsText: string
  isVetDiet: boolean
  refillableSectionRef: RefObject<SwapProductRefillHandle> | null
  setShowValidationError: (show: boolean) => void
  taskSectionRef: RefObject<TaskSectionHandle> | null
}

export const validateTaskSection = (
  dependencies: SwapProductValidationDependencies,
): boolean => {
  const { automaticallyCreateTaskValue, taskSectionRef } = dependencies
  if (!automaticallyCreateTaskValue) return true
  if (!taskSectionRef?.current) return true
  return taskSectionRef.current.validate()
}

export const validateRefillableSection = (
  dependencies: SwapProductValidationDependencies,
): boolean => {
  const { refillableSectionRef } = dependencies
  if (!refillableSectionRef?.current) return true
  return refillableSectionRef.current.validate()
}

const getAllFormFields = (form: HTMLFormElement): NodeListOf<FormField> =>
  form.querySelectorAll<FormField>('input, select, textarea')

const getRequiredFormFields = (form: HTMLFormElement): NodeListOf<FormField> =>
  form.querySelectorAll<FormField>(
    'input[required], select[required], textarea[required]',
  )

const clearCustomValidityOnFields = (fields: NodeListOf<FormField>): void => {
  fields.forEach((field) => {
    field.setCustomValidity('')
  })
}

const pendingRestoreTimeouts = new WeakMap<
  HTMLFormElement,
  ReturnType<typeof setTimeout>
>()

const restoreRequiredAttributes = (
  requiredFields: Set<FormField>,
  formRef: RefObject<HTMLFormElement>,
): void => {
  if (!formRef.current) return

  const existingTimeout = pendingRestoreTimeouts.get(formRef.current)
  if (existingTimeout) {
    clearTimeout(existingTimeout)
  }

  const timeoutId = setTimeout(() => {
    if (!formRef.current) return
    pendingRestoreTimeouts.delete(formRef.current)

    requiredFields.forEach((field) => {
      if (formRef.current?.contains(field)) {
        field.setAttribute('required', '')
      }
    })
  }, 0)

  pendingRestoreTimeouts.set(formRef.current, timeoutId)
}

const handleValidationError = (
  setShowValidationError: (show: boolean) => void,
): boolean => {
  setShowValidationError(true)
  return false
}

/**
 * Validates swap product form with action-specific rules:
 * - DRAFT: No validation (removes required attributes to prevent CSS :invalid state)
 * - DECLINED: Only validates doctor field (removes required from other fields)
 * - APPROVED/undefined: Validates all fields
 */
export const validateSwapProductForm = (
  actionOrOptions: PrescriptionStates | undefined,
  dependencies: SwapProductValidationDependencies,
): boolean => {
  const {
    formRef,
    doctorFieldRef,
    doctorIdValue,
    doctorValidationMessage,
    isVetDiet,
    instructionsText,
    setShowValidationError,
  } = dependencies

  if (actionOrOptions === PrescriptionStates.DRAFT) {
    if (formRef.current) {
      const requiredFields = getRequiredFormFields(formRef.current)
      const requiredFieldsSet = new Set<FormField>()

      requiredFields.forEach((field) => {
        requiredFieldsSet.add(field)
        field.removeAttribute('required')
      })

      const allFields = getAllFormFields(formRef.current)
      clearCustomValidityOnFields(allFields)

      restoreRequiredAttributes(requiredFieldsSet, formRef)
    }
    setShowValidationError(false)
    return true
  }

  if (actionOrOptions === PrescriptionStates.DECLINED) {
    if (!formRef.current) {
      setShowValidationError(false)
      return true
    }

    const doctorField = dependencies.doctorFieldRef.current

    if (!doctorField) {
      setShowValidationError(false)
      return true
    }

    const requiredFields = getRequiredFormFields(formRef.current)
    const requiredFieldsSet = new Set<FormField>()

    requiredFields.forEach((field) => {
      if (field !== doctorField) {
        requiredFieldsSet.add(field)
        field.removeAttribute('required')
      }
    })

    const allFields = getAllFormFields(formRef.current)
    clearCustomValidityOnFields(allFields)

    if (!doctorIdValue) {
      doctorField.setCustomValidity(doctorValidationMessage)
      doctorField.checkValidity()
      requiredFieldsSet.forEach((field) => {
        if (formRef.current?.contains(field)) {
          field.setAttribute('required', '')
        }
      })
      return handleValidationError(setShowValidationError)
    }

    restoreRequiredAttributes(requiredFieldsSet, formRef)

    setShowValidationError(false)
    return true
  }

  if (
    (formRef.current && !formRef.current.reportValidity()) ||
    (isVetDiet && !instructionsText) ||
    !validateRefillableSection(dependencies) ||
    !validateTaskSection(dependencies)
  ) {
    return handleValidationError(setShowValidationError)
  }

  setShowValidationError(false)
  return true
}
